import { supabase } from '../lib/supabase';
import { MatchWithTeams } from '../types/database';
import { QueryPerformanceMonitor } from '../utils/queryOptimizer';

// Types for access control
export interface TournamentAccess {
  tournament_id: string;
  access_type: 'participant' | 'viewer' | 'organizer';
}

export interface AppAccess {
  app_name: string;
  access_level: 'view' | 'edit' | 'admin';
}

export interface UserOrganisation {
  organisation_id: string;
  role: string;
  is_primary: boolean;
}

export interface AccessControlResult<T> {
  data: T | null;
  error: string | null;
}

/**
 * Centralized Access Control Service
 * Handles all permission-based data fetching with RLS compliance
 */
export class AccessControlService {
  private static instance: AccessControlService;
  private userCache: Map<string, any> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private organizationCache: Map<string, any> = new Map();
  private orgCacheTimeout = 10 * 60 * 1000; // 10 minutes for organization data
  private pendingRequests = new Map<string, Promise<AccessControlResult<any>>>();

  static getInstance(): AccessControlService {
    if (!AccessControlService.instance) {
      AccessControlService.instance = new AccessControlService();
    }
    return AccessControlService.instance;
  }

  /**
   * Get current authenticated user ID
   */
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Get current organization from context (if available)
   */
  private currentOrgId: string | null = null;
  private organizationData: any = null;

  setCurrentOrganization(orgId: string | null): void {
    if (this.currentOrgId !== orgId) {
      this.currentOrgId = orgId;
      // Clear cache when organization changes
      this.clearCache();
      this.organizationData = null;
    }
  }

  /**
   * Set organization data to avoid repeated API calls
   */
  setOrganizationData(orgData: any): void {
    const newOrgId = orgData?.id;
    
    // Clear cache when organization changes
    if (this.currentOrgId !== newOrgId) {
      this.clearCache();
    }
    
    this.organizationData = orgData;
    this.currentOrgId = newOrgId;
  }

  /**
   * Get user's organizations with enhanced caching and request deduplication
   */
  async getUserOrganisations(userId?: string): Promise<AccessControlResult<UserOrganisation[]>> {
    try {
      const currentUserId = userId || await this.getCurrentUserId();
      if (!currentUserId) {
        return { data: null, error: 'User not authenticated' };
      }

      const cacheKey = `user_orgs_${currentUserId}_${this.currentOrgId || 'all'}`;
      
      // Check organization cache first (longer TTL)
      const orgCached = this.getOrgCachedData(cacheKey);
      if (orgCached) {
        console.log('📦 Using cached organization data for user:', currentUserId);
        return { data: orgCached, error: null };
      }

      // Check user cache as fallback
      const userCached = this.getCachedData(cacheKey);
      if (userCached) {
        console.log('📦 Using cached user data for user:', currentUserId);
        return { data: userCached, error: null };
      }

      // Check if there's already a pending request for this user
      if (this.pendingRequests.has(cacheKey)) {
        console.log('⏳ Waiting for pending organization request for user:', currentUserId);
        return this.pendingRequests.get(cacheKey)!;
      }

      console.log('🔄 Fetching fresh organization data for user:', currentUserId);

      // Create a promise for this request
      const requestPromise = this.fetchUserOrganisations(currentUserId, cacheKey);
      this.pendingRequests.set(cacheKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up pending request
        this.pendingRequests.delete(cacheKey);
      }
    } catch (error) {
      console.error('Error in getUserOrganisations:', error);
      return { data: null, error: 'Failed to fetch user organizations' };
    }
  }

  /**
   * Internal method to fetch user organizations
   */
  private async fetchUserOrganisations(currentUserId: string, cacheKey: string): Promise<AccessControlResult<UserOrganisation[]>> {
    let query = supabase
      .from('user_organisations')
      .select('organisation_id, role, is_primary')
      .eq('user_id', currentUserId)
      .eq('active', true);

    // If specific organization is set, filter by it
    if (this.currentOrgId) {
      query = query.eq('organisation_id', this.currentOrgId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user organizations:', error);
      return { data: null, error: error.message };
    }

    const result = data || [];
    
    // Cache in both caches for different TTLs
    this.setOrgCachedData(cacheKey, result);
    this.setCachedData(cacheKey, result);
    
    console.log('✅ Organization data cached for user:', currentUserId);
    return { data: result, error: null };
  }

  /**
   * Get user's accessible tournaments
   */
  async getAccessibleTournaments(userId?: string): Promise<AccessControlResult<TournamentAccess[]>> {
    try {
      const userOrgsResult = await this.getUserOrganisations(userId);
      if (userOrgsResult.error || !userOrgsResult.data) {
        return { data: null, error: userOrgsResult.error };
      }

      const orgIds = userOrgsResult.data.map(org => org.organisation_id);
      if (orgIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `accessible_tournaments_${orgIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('tournament_access')
        .select('tournament_id, access_type')
        .in('organisation_id', orgIds)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching accessible tournaments:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getAccessibleTournaments:', error);
      return { data: null, error: 'Failed to fetch accessible tournaments' };
    }
  }

  /**
   * Get user's accessible apps with enhanced caching
   */
  async getAccessibleApps(userId?: string): Promise<AccessControlResult<AppAccess[]>> {
    try {
      const currentUserId = userId || await this.getCurrentUserId();
      if (!currentUserId) {
        return { data: null, error: 'User not authenticated' };
      }

      // Use current organization if set
      let orgIdToUse = this.currentOrgId;
      
      if (!orgIdToUse) {
        // Fallback to primary organization
        const userOrgsResult = await this.getUserOrganisations(currentUserId);
        if (userOrgsResult.error || !userOrgsResult.data) {
          return { data: null, error: userOrgsResult.error };
        }
        
        const primaryOrg = userOrgsResult.data.find(org => org.is_primary);
        if (primaryOrg) {
          orgIdToUse = primaryOrg.organisation_id;
        } else if (userOrgsResult.data.length > 0) {
          orgIdToUse = userOrgsResult.data[0].organisation_id;
        } else {
          return { data: [], error: null };
        }
      }

      const cacheKey = `accessible_apps_${orgIdToUse}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        console.log('📦 Using cached app access data for org:', orgIdToUse);
        return { data: cached, error: null };
      }

      console.log('🔄 Fetching fresh app access data for org:', orgIdToUse);
      const { data, error } = await supabase
        .from('organisation_app_access')
        .select('app_name, access_level')
        .eq('organisation_id', orgIdToUse)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching accessible apps:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      console.log('✅ App access data cached for org:', orgIdToUse);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getAccessibleApps:', error);
      return { data: null, error: 'Failed to fetch accessible apps' };
    }
  }

  /**
   * Get matches with team information (access-controlled)
   */
  async getMatchesWithTeams(userId?: string): Promise<AccessControlResult<MatchWithTeams[]>> {
    try {
      const tournamentsResult = await this.getAccessibleTournaments(userId);
      if (tournamentsResult.error || !tournamentsResult.data) {
        return { data: null, error: tournamentsResult.error };
      }

      const tournamentIds = tournamentsResult.data.map(t => t.tournament_id);
      if (tournamentIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `matches_with_teams_${tournamentIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const queryId = `getMatchesWithTeams_${Date.now()}`;
      QueryPerformanceMonitor.startQuery(queryId);

      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          home_team:home_team_id(
            id,
            name,
            logo,
            venue,
            country,
            city
          ),
          away_team:away_team_id(
            id,
            name,
            logo,
            venue,
            country,
            city
          ),
          tournament:tournament_id(
            id,
            name,
            logo,
            country
          )
        `)
        .in('tournament_id', tournamentIds)
        .eq('active', true)
        .order('start_date', { ascending: false });

      const duration = QueryPerformanceMonitor.endQuery(queryId);
      console.log(`📊 getMatchesWithTeams query completed in ${duration}ms`);

      if (error) {
        console.error('Error fetching matches with teams:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getMatchesWithTeams:', error);
      return { data: null, error: 'Failed to fetch matches with teams' };
    }
  }

  /**
   * Get tournaments (access-controlled)
   */
  async getTournaments(userId?: string): Promise<AccessControlResult<any[]>> {
    try {
      const tournamentsResult = await this.getAccessibleTournaments(userId);
      if (tournamentsResult.error || !tournamentsResult.data) {
        return { data: null, error: tournamentsResult.error };
      }

      const tournamentIds = tournamentsResult.data.map(t => t.tournament_id);
      if (tournamentIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `tournaments_${tournamentIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, logo, country, data_source')
        .in('id', tournamentIds)
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching tournaments:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getTournaments:', error);
      return { data: null, error: 'Failed to fetch tournaments' };
    }
  }

  /**
   * Get complete tournament details by ID (access-controlled)
   */
  async getTournamentDetails(tournamentId: string, userId?: string): Promise<AccessControlResult<any>> {
    try {
      // First check if user has access to this tournament
      const hasAccess = await this.hasTournamentAccess(tournamentId, userId);
      if (!hasAccess) {
        return { data: null, error: 'Access denied to tournament' };
      }

      const cacheKey = `tournament_details_${tournamentId}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', tournamentId)
        .single();

      if (error) {
        console.error('Error fetching tournament details:', error);
        return { data: null, error: error.message };
      }

      this.setCachedData(cacheKey, data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in getTournamentDetails:', error);
      return { data: null, error: 'Failed to fetch tournament details' };
    }
  }

  /**
   * Update tournament details (access-controlled)
   */
  async updateTournamentDetails(tournamentId: string, updates: any, userId?: string): Promise<AccessControlResult<any>> {
    try {
      // First check if user has access to this tournament
      const hasAccess = await this.hasTournamentAccess(tournamentId, userId);
      if (!hasAccess) {
        return { data: null, error: 'Access denied to tournament' };
      }

      // Add updated_at timestamp
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('tournaments')
        .update(updateData)
        .eq('id', tournamentId)
        .select()
        .single();

      if (error) {
        console.error('Error updating tournament details:', error);
        return { data: null, error: error.message };
      }

      // Clear cache for this tournament
      const cacheKey = `tournament_details_${tournamentId}`;
      this.userCache.delete(cacheKey);

      return { data, error: null };
    } catch (error) {
      console.error('Error in updateTournamentDetails:', error);
      return { data: null, error: 'Failed to update tournament details' };
    }
  }

  /**
   * Get teams (access-controlled)
   */
  async getTeams(userId?: string): Promise<AccessControlResult<any[]>> {
    try {
      const userOrgsResult = await this.getUserOrganisations(userId);
      if (userOrgsResult.error || !userOrgsResult.data) {
        return { data: null, error: userOrgsResult.error };
      }

      const orgIds = userOrgsResult.data.map(org => org.organisation_id);
      if (orgIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `teams_${orgIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .or(`organisation_id.in.(${orgIds.join(',')}),data_source.eq.official`)
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching teams:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getTeams:', error);
      return { data: null, error: 'Failed to fetch teams' };
    }
  }

  /**
   * Get match details with access control
   */
  async getMatchDetails(matchId: string, userId?: string): Promise<AccessControlResult<any>> {
    try {
      const tournamentsResult = await this.getAccessibleTournaments(userId);
      if (tournamentsResult.error || !tournamentsResult.data) {
        return { data: null, error: tournamentsResult.error };
      }

      const tournamentIds = tournamentsResult.data.map(t => t.tournament_id);
      if (tournamentIds.length === 0) {
        return { data: null, error: 'No accessible tournaments' };
      }

      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          home_team:teams!home_team_id(*),
          away_team:teams!away_team_id(*),
          tournament:tournaments(*),
          match_officials:match_officials(
            *,
            official:officials(*)
          )
        `)
        .eq('id', matchId)
        .in('tournament_id', tournamentIds)
        .single();

      if (error) {
        console.error('Error fetching match details:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in getMatchDetails:', error);
      return { data: null, error: 'Failed to fetch match details' };
    }
  }

  /**
   * Check if user has access to a specific app
   */
  async hasAppAccess(appName: string, userId?: string): Promise<boolean> {
    try {
      const appsResult = await this.getAccessibleApps(userId);
      if (appsResult.error || !appsResult.data) {
        return false;
      }

      return appsResult.data.some(app => app.app_name === appName);
    } catch (error) {
      console.error('Error checking app access:', error);
      return false;
    }
  }

  /**
   * Check if user has access to a specific tournament
   */
  async hasTournamentAccess(tournamentId: string, userId?: string): Promise<boolean> {
    try {
      const tournamentsResult = await this.getAccessibleTournaments(userId);
      if (tournamentsResult.error || !tournamentsResult.data) {
        return false;
      }

      return tournamentsResult.data.some(t => t.tournament_id === tournamentId);
    } catch (error) {
      console.error('Error checking tournament access:', error);
      return false;
    }
  }

  /**
   * Clear cache for a specific user
   */
  clearUserCache(userId?: string): void {
    if (userId) {
      const keysToDelete = Array.from(this.userCache.keys()).filter(key => 
        key.includes(userId)
      );
      keysToDelete.forEach(key => this.userCache.delete(key));
    } else {
      this.userCache.clear();
    }
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.userCache.clear();
    this.organizationCache.clear();
  }

  /**
   * Enhanced cache management methods
   */
  private getCachedData(key: string): any {
    const cached = this.userCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  private setCachedData(key: string, data: any): void {
    this.userCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  private getOrgCachedData(key: string): any {
    const cached = this.organizationCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.orgCacheTimeout) {
      return cached.data;
    }
    return null;
  }

  private setOrgCachedData(key: string, data: any): void {
    this.organizationCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Smart cache invalidation
   */
  private invalidateRelatedCaches(userId?: string, orgId?: string): void {
    if (userId) {
      // Clear user-specific caches
      const userKeys = Array.from(this.userCache.keys()).filter(key => 
        key.includes(userId)
      );
      userKeys.forEach(key => this.userCache.delete(key));
    }
    
    if (orgId) {
      // Clear organization-specific caches
      const orgKeys = Array.from(this.organizationCache.keys()).filter(key => 
        key.includes(orgId)
      );
      orgKeys.forEach(key => this.organizationCache.delete(key));
    }
  }
  /**
   * Get user's accessible teams
   */
  async getAccessibleTeams(userId?: string): Promise<AccessControlResult<any[]>> {
    try {
      const userOrgsResult = await this.getUserOrganisations(userId);
      if (userOrgsResult.error || !userOrgsResult.data) {
        return { data: null, error: userOrgsResult.error };
      }

      const orgIds = userOrgsResult.data.map(org => org.organisation_id);
      if (orgIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `accessible_teams_${orgIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('team_access')
        .select('team_id, access_type')
        .in('organisation_id', orgIds)
        .eq('active', true);

      if (error) {
        console.error('Error fetching accessible teams:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getAccessibleTeams:', error);
      return { data: null, error: 'Failed to fetch accessible teams' };
    }
  }

  /**
   * Get user's accessible players
   */
  async getAccessiblePlayers(userId?: string): Promise<AccessControlResult<any[]>> {
    try {
      const userOrgsResult = await this.getUserOrganisations(userId);
      if (userOrgsResult.error || !userOrgsResult.data) {
        return { data: null, error: userOrgsResult.error };
      }

      const orgIds = userOrgsResult.data.map(org => org.organisation_id);
      if (orgIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `accessible_players_${orgIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('player_access')
        .select('player_id, access_type')
        .in('organisation_id', orgIds)
        .eq('active', true);

      if (error) {
        console.error('Error fetching accessible players:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getAccessiblePlayers:', error);
      return { data: null, error: 'Failed to fetch accessible players' };
    }
  }

  /**
   * Get user's accessible seasons
   */
  async getAccessibleSeasons(userId?: string): Promise<AccessControlResult<any[]>> {
    try {
      const userOrgsResult = await this.getUserOrganisations(userId);
      if (userOrgsResult.error || !userOrgsResult.data) {
        return { data: null, error: userOrgsResult.error };
      }

      const orgIds = userOrgsResult.data.map(org => org.organisation_id);
      if (orgIds.length === 0) {
        return { data: [], error: null };
      }

      const cacheKey = `accessible_seasons_${orgIds.join(',')}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return { data: cached, error: null };
      }

      const { data, error } = await supabase
        .from('season_access')
        .select('season_id, access_type')
        .in('organisation_id', orgIds)
        .eq('active', true);

      if (error) {
        console.error('Error fetching accessible seasons:', error);
        return { data: null, error: error.message };
      }

      const result = data || [];
      this.setCachedData(cacheKey, result);
      return { data: result, error: null };
    } catch (error) {
      console.error('Error in getAccessibleSeasons:', error);
      return { data: null, error: 'Failed to fetch accessible seasons' };
    }
  }

  /**
   * Check if user has access to a specific team
   */
  async hasTeamAccess(teamId: string, userId?: string): Promise<boolean> {
    try {
      const accessibleTeams = await this.getAccessibleTeams(userId);
      if (accessibleTeams.error || !accessibleTeams.data) {
        return false;
      }
      return accessibleTeams.data.some(access => access.team_id === teamId);
    } catch (error) {
      console.error('Error checking team access:', error);
      return false;
    }
  }

  /**
   * Check if user has access to a specific player
   */
  async hasPlayerAccess(playerId: string, userId?: string): Promise<boolean> {
    try {
      const accessiblePlayers = await this.getAccessiblePlayers(userId);
      if (accessiblePlayers.error || !accessiblePlayers.data) {
        return false;
      }
      return accessiblePlayers.data.some(access => access.player_id === playerId);
    } catch (error) {
      console.error('Error checking player access:', error);
      return false;
    }
  }

  /**
   * Check if user has access to a specific season
   */
  async hasSeasonAccess(seasonId: string, userId?: string): Promise<boolean> {
    try {
      const accessibleSeasons = await this.getAccessibleSeasons(userId);
      if (accessibleSeasons.error || !accessibleSeasons.data) {
        return false;
      }
      return accessibleSeasons.data.some(access => access.season_id === seasonId);
    } catch (error) {
      console.error('Error checking season access:', error);
      return false;
    }
  }
}

// Export singleton instance
export const accessControlService = AccessControlService.getInstance();
